<template>
    <div class="font-calibration-tool">
        <!-- 1. Text Wrapping Preview Tool -->
        <div class="calibration-section">
            <h2>Text Wrapping Preview Tool</h2>

            <div class="control-panel">
                <div class="form-row">
                    <div class="form-group">
                        <label>Font Family:</label>
                        <el-select
                            v-model="previewSettings.font"
                            placeholder="Select Font"
                        >
                            <el-option
                                v-for="(name, key) in fonts"
                                :key="key"
                                :label="name"
                                :value="name"
                                :style="{ fontFamily: name }"
                            ></el-option>
                        </el-select>
                    </div>

                    <div class="form-group">
                        <label>Font Size:</label>
                        <el-select
                            v-model="previewSettings.fontSize"
                            placeholder="Size"
                        >
                            <el-option
                                v-for="size in fontSizes"
                                :key="size"
                                :label="`${size}pt`"
                                :value="size"
                            ></el-option>
                        </el-select>
                    </div>

                    <div class="form-group checkbox-group">
                        <label>Bold:</label>
                        <el-switch v-model="previewSettings.isBold"></el-switch>
                    </div>

                    <div class="form-group checkbox-group">
                        <label>Show Time:</label>
                        <el-switch
                            v-model="previewSettings.showTime"
                        ></el-switch>
                    </div>

                    <div class="form-group" v-if="previewSettings.showTime">
                        <label>Time:</label>
                        <el-time-picker
                            v-model="previewSettings.timeValue"
                            format="h:mm A"
                            placeholder="Select time"
                            :clearable="false"
                            @change="updatePreview"
                        ></el-time-picker>
                    </div>

                    <div class="form-group">
                        <label>Adjustment Value:</label>
                        <el-input-number
                            v-model="previewSettings.adjustment"
                            :min="-40"
                            :max="40"
                            :step="1"
                            @change="updatePreview"
                        ></el-input-number>
                        <span class="hint"
                            >(-) increases width, (+) decreases width</span
                        >
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group sample-text-group">
                        <label>Sample Text:</label>
                        <el-input
                            v-model="previewSettings.sampleText"
                            type="textarea"
                            :rows="2"
                            placeholder="Enter text to test wrapping"
                        ></el-input>

                        <div class="example-texts">
                            <el-button
                                size="mini"
                                v-for="(text, idx) in exampleTexts"
                                :key="idx"
                                @click="previewSettings.sampleText = text"
                                >{{ text }}</el-button
                            >
                        </div>
                    </div>
                </div>
            </div>

            <!-- Preview Area -->
            <div class="preview-area">
                <div class="actions">
                    <el-button type="primary" @click="updatePreview"
                        >Update Preview</el-button
                    >
                </div>

                <div class="preview-container">
                    <h3>Line Wrapping Results</h3>

                    <!-- Calendar cell representation -->
                    <div
                        class="calendar-cell"
                        :class="'font-size-' + previewSettings.fontSize"
                    >
                        <div class="date-number">15</div>
                        <div class="cell-content">
                            <!-- Add this width boundary box -->
                            <div
                                class="width-boundary"
                                :style="{
                                    width: `${effectiveMaxWidth}px`,
                                    height: '100%',
                                    position: 'absolute',
                                    left: previewSettings.showTime
                                        ? `${timeColumnWidth}px`
                                        : '0',
                                    borderRight: '1px dashed #f56c6c',
                                    boxSizing: 'border-box',
                                    pointerEvents: 'none',
                                    zIndex: 1
                                }"
                            ></div>
                            <!-- Each line represents a separate event in the calendar -->
                            <div
                                class="description"
                                v-if="previewLines.length > 0"
                            >
                                <!-- Time section (fixed width, left floated) -->
                                <div
                                    class="time float-left text-right"
                                    :style="{
                                        width: `${timeColumnWidth}px`,
                                        maxWidth: `${timeColumnWidth}px`
                                    }"
                                >
                                    <span
                                        v-if="previewSettings.showTime"
                                        :style="previewTextStyle"
                                    >
                                        {{ formattedTime }}&nbsp;&nbsp;
                                    </span>
                                </div>

                                <!-- Description lines section (right floated with fixed width) -->
                                <div
                                    class="description-lines float-right event-title"
                                    :style="{
                                        width: `${descriptionColumnWidth}px`
                                    }"
                                >
                                    <div
                                        v-for="(line, idx) in previewLines"
                                        :key="idx"
                                        :style="previewTextStyle"
                                    >
                                        {{ line }}
                                    </div>
                                </div>

                                <!-- Clear floats -->
                                <div class="clearfix"></div>
                            </div>
                        </div>
                    </div>

                    <!-- Width indicator -->
                    <div class="width-indicators">
                        <div
                            class="max-width-line"
                            :style="{ width: `${effectiveMaxWidth}px` }"
                        ></div>
                        <div class="max-width-label">
                            Max Width: {{ effectiveMaxWidth }}px
                            <span
                                v-if="previewSettings.adjustment !== 0"
                                class="adjustment-note"
                            >
                                (Base {{ maxWidth }}px, adjustment
                                {{ previewSettings.adjustment > 0 ? "-" : "+"
                                }}{{ Math.abs(previewSettings.adjustment) }}px)
                            </span>
                        </div>

                        <div class="width-info">
                            <div v-if="previewLines.length > 0">
                                <strong>Lines:</strong>
                                {{ previewLines.length }}
                            </div>
                            <div v-if="totalTextWidth">
                                <strong>Total Width:</strong>
                                {{ totalTextWidth }}px ({{
                                    Math.round(
                                        (totalTextWidth / maxWidth) * 100
                                    )
                                }}% of max)
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 2. Font Adjustment Manager -->
        <div class="calibration-section" v-if="isStaffUser">
            <h2>Font Width Adjustment Manager</h2>

            <div class="browser-selector">
                <label>Browser:</label>
                <el-select
                    v-model="selectedBrowser"
                    placeholder="Select Browser"
                >
                    <el-option
                        v-for="browser in browsers"
                        :key="browser"
                        :label="browserLabels[browser] || browser"
                        :value="browser"
                    ></el-option>
                </el-select>

                <label class="ml-10">Font Size:</label>
                <el-select
                    v-model="selectedFontSize"
                    placeholder="Select Font Size"
                >
                    <el-option
                        v-for="size in fontSizes"
                        :key="size"
                        :label="`${size}pt`"
                        :value="size"
                    ></el-option>
                </el-select>

                <span class="detected-browser ml-10">
                    Detected Browser: {{ detectedBrowser }}
                </span>
            </div>

            <!-- Font adjustment editor -->
            <div
                v-if="selectedBrowser && selectedFontSize && currentAdjustments"
                class="font-adjustments-editor"
            >
                <div class="font-adjustments-grid">
                    <div
                        v-for="(adjustment, fontName) in currentAdjustments"
                        :key="fontName"
                        class="font-adjustment-row"
                    >
                        <span
                            class="font-name"
                            :style="{ fontFamily: fontName }"
                            >{{ fontName }}:</span
                        >
                        <el-input-number
                            v-model="currentAdjustments[fontName]"
                            :min="-40"
                            :max="40"
                            :step="1"
                        ></el-input-number>
                    </div>
                </div>

                <div class="adjustment-actions">
                    <el-button type="primary" @click="saveAdjustments"
                        >Save Adjustments</el-button
                    >
                    <el-button @click="resetAdjustments">Reset</el-button>
                    <el-button
                        type="success"
                        @click="applyForTesting"
                        v-if="!isTestingMode"
                    >
                        Apply For Testing
                    </el-button>
                    <el-button
                        type="warning"
                        @click="restoreOriginalSettings"
                        v-if="isTestingMode"
                    >
                        Restore Original Settings
                    </el-button>
                </div>
            </div>
        </div>

        <!-- 3. Automatic Calibration Tool -->
        <div class="calibration-section" v-if="isStaffUser">
            <h2>Automatic Width Calibration</h2>

            <div class="calibration-controls">
                <el-button
                    @click="runFullCalibration"
                    type="primary"
                    :loading="calibrationRunning"
                >
                    Run Complete Font Calibration
                </el-button>

                <div class="calibration-info">
                    This will test each font with sample text and determine
                    optimal adjustment values. The process may take several
                    seconds.
                </div>
            </div>

            <!-- Results display -->
            <div v-if="formattedResults" class="calibration-results">
                <h3>Calibration Results</h3>
                <pre class="results-code">{{ formattedResults }}</pre>
                <div class="result-actions">
                    <el-button
                        @click="copyToClipboard"
                        size="small"
                        type="success"
                    >
                        Copy to Clipboard
                    </el-button>
                    <el-button
                        @click="saveCalibrationResults"
                        size="small"
                        type="primary"
                    >
                        Save These Values
                    </el-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as Fonts from "@/constants/calendar-fonts";
import * as ROLES from "@/constants/roles";
import FontService from "@/services/font-service";
import FontLoader from "@/services/FontLoader";
import Utils from "@/utils";
import CalendarUtils from "@/utils/calendar";
import FontCalibration from "@/utils/font-calibration";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
    name: "FontCalibrationTool",
    props: {
        // Using props to receive boldMap from parent
        boldMap: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            // Preview tool settings
            previewSettings: {
                font: "Modern Medium", // Default font
                fontSize: 10,
                isBold: false,
                adjustment: 2,
                sampleText: "Mexican Train Dominoes-SH",
                showTime: true,
                timeValue: new Date(2023, 1, 1, 9, 30)
            },
            previewLines: [],
            totalTextWidth: 0,
            maxWidth: 167, // Default for font-size-10

            // Example texts for quick testing
            exampleTexts: [
                "Mexican Train Dominoes-SH",
                "Bible Study W/ Scott-Library",
                "Thanksgiving Dinner-Clubhouse",
                "November Birthday Dessert Bar (DR)"
            ],

            // Adjustment manager data
            browsers: ["chrome", "firefox", "edge", "safari"],
            browserLabels: {
                chrome: "Chrome",
                firefox: "Firefox",
                edge: "Edge",
                safari: "Safari"
            },
            fontSizes: [7, 8, 9, 10, 11, 12],
            selectedBrowser: null,
            selectedFontSize: null,
            currentAdjustments: null,
            detectedBrowser: "",
            isTestingMode: false, // Track if we're in testing mode
            originalFontMethod: null, // Store the original method

            // Calibration tool data
            calibrationRunning: false,
            formattedResults: null,

            // Font reference
            fonts: Fonts
        };
    },

    computed: {
        ...mapState({
            fontAdjustments: (state) => state.fontAdjustments.adjustments
        }),
        ...mapGetters(["hasAnyRole"]),

        previewTextStyle() {
            const fontName = this.previewSettings.isBold
                ? this.boldMap[this.previewSettings.font] ||
                  this.previewSettings.font
                : this.previewSettings.font;

            const fontWeight =
                FontService.getFontWeightFromCSS(fontName) ||
                (this.previewSettings.isBold ? "bold" : "normal");

            return {
                fontFamily: fontName,
                fontSize: `${this.previewSettings.fontSize}pt`,
                fontWeight: fontWeight,
                color: "#000",
                whiteSpace: "nowrap"
            };
        },

        isStaffUser() {
            return this.hasAnyRole(ROLES.MAIN_ROLES);
        },

        formattedTime() {
            if (!this.previewSettings.timeValue) return "";
            const date = new Date(this.previewSettings.timeValue);
            const hours = date.getHours();
            const minutes = date.getMinutes();
            const ampm = hours >= 12 ? "pm" : "am";
            const hour = hours % 12 || 12;
            const minute = minutes < 10 ? "0" + minutes : minutes;
            return `${hour}:${minute}`;
        },

        timeColumnWidth() {
            // Match the exact widths from Calendar.vue
            if (this.previewSettings.fontSize >= 11) {
                return 50;
            } else if (this.previewSettings.fontSize >= 9) {
                return 42;
            } else {
                return 35;
            }
        },

        descriptionColumnWidth() {
            // Match the exact widths from Calendar.vue
            if (this.previewSettings.fontSize >= 11) {
                return 158;
            } else if (this.previewSettings.fontSize >= 9) {
                return 165;
            } else {
                return 174;
            }
        },

        effectiveMaxWidth() {
            // Calculate the effective max width based on the adjustment value
            // Negative adjustment increases width, positive decreases
            const adjustmentEffect = this.previewSettings.adjustment * -1;
            return this.descriptionColumnWidth + adjustmentEffect;
        }
    },

    mounted() {
        // Load fonts and detect browser on mount
        FontLoader.preloadDropdownFonts();
        this.detectBrowser();

        // Set initial browser selection to detected browser
        const browserMap = {
            Firefox: "firefox",
            Edge: "edge",
            Safari: "safari",
            Chrome: "chrome"
        };

        this.selectedBrowser = browserMap[this.detectedBrowser] || "chrome";
        this.selectedFontSize = 10; // Default font size

        // Run initial preview
        this.updatePreview();
    },

    watch: {
        selectedBrowser() {
            this.updateCurrentAdjustments();
        },

        selectedFontSize() {
            this.updateCurrentAdjustments();
            this.updateMaxWidth();
        },

        fontAdjustments: {
            handler() {
                this.updateCurrentAdjustments();
            },
            deep: true
        },

        "previewSettings.fontSize"() {
            this.updateMaxWidth();
        }
    },

    methods: {
        ...mapActions("fontAdjustments", [
            "fetchFontAdjustments",
            "updateFontAdjustments"
        ]),

        detectBrowser() {
            const userAgent = navigator.userAgent;
            if (userAgent.indexOf("Firefox") > -1) {
                this.detectedBrowser = "Firefox";
            } else if (userAgent.indexOf("Edg") > -1) {
                this.detectedBrowser = "Edge";
            } else if (
                userAgent.indexOf("Chrome") === -1 &&
                userAgent.indexOf("Safari") > -1
            ) {
                this.detectedBrowser = "Safari";
            } else if (userAgent.indexOf("Chrome") > -1) {
                this.detectedBrowser = "Chrome";
            } else {
                this.detectedBrowser = "Unknown";
            }
        },

        updateMaxWidth() {
            // Update maxWidth based on font size using calendar's own logic
            // switch (this.previewSettings.fontSize) {
            //     case 7:
            //     case 8:
            //         this.maxWidth = 174;
            //         break;
            //     case 9:
            //         this.maxWidth = 170;
            //         break;
            //     case 10:
            //         this.maxWidth = 167;
            //         break;
            //     case 11:
            //     case 12:
            //         this.maxWidth = 164;
            //         break;
            //     default:
            //         this.maxWidth = 167;
            // }

            // Update preview with new maxWidth
            this.updatePreview();
        },

        updatePreview() {
            if (!this.previewSettings.sampleText) return;

            // Ensure fonts are loaded before calculating
            FontLoader.loadFont(this.previewSettings.font).then(() => {
                // Create a mock event for calculation
                const mockEvent = {
                    description: this.previewSettings.sampleText,
                    is_bold: this.previewSettings.isBold,
                    is_all_day: !this.previewSettings.showTime,
                    time: this.previewSettings.showTime
                        ? this.previewSettings.timeValue
                        : null
                };

                // Override font adjustment temporarily if needed for testing
                const originalGetFontAdjustments =
                    CalendarUtils.getFontAdjustments;

                // Only override if adjustment isn't the default
                if (this.previewSettings.adjustment !== 2) {
                    CalendarUtils.getFontAdjustments = (fontSize) => {
                        const fontName = this.previewSettings.isBold
                            ? this.boldMap[this.previewSettings.font] ||
                              this.previewSettings.font
                            : this.previewSettings.font;

                        // Create a custom adjustment object with just our test font
                        const adjObj = {};
                        adjObj[fontName] = this.previewSettings.adjustment;
                        return adjObj;
                    };
                }

                // Clear the line cache for accurate calculation
                CalendarUtils.clearDescriptionLinesCache();

                // Get wrapped lines
                this.previewLines = CalendarUtils.getEventDescriptionLines(
                    mockEvent,
                    this.previewSettings.font,
                    this.previewSettings.fontSize
                );

                // Calculate total text width
                const fontName = this.previewSettings.isBold
                    ? this.boldMap[this.previewSettings.font] ||
                      this.previewSettings.font
                    : this.previewSettings.font;

                this.totalTextWidth = Utils.getTextWidth(
                    this.previewSettings.sampleText,
                    fontName,
                    this.previewSettings.fontSize,
                    FontService.getFontWeightFromCSS(fontName)
                );

                // Restore original function
                if (this.previewSettings.adjustment !== 2) {
                    CalendarUtils.getFontAdjustments =
                        originalGetFontAdjustments;
                }
            });
        },

        updateCurrentAdjustments() {
            if (
                !this.fontAdjustments ||
                !this.selectedBrowser ||
                !this.selectedFontSize
            ) {
                this.currentAdjustments = null;
                return;
            }

            // Get adjustments for selected browser and font size
            const browserAdjustments =
                this.fontAdjustments[this.selectedBrowser] || {};
            const sizeAdjustments =
                browserAdjustments[this.selectedFontSize] || {};

            // Create a copy of the adjustments or initialize with defaults for all fonts
            const adjustments = {};

            // Add all fonts with default values if they don't exist
            Object.values(Fonts).forEach((fontName) => {
                adjustments[fontName] = sizeAdjustments[fontName] || 2; // Default to 2
            });

            this.currentAdjustments = adjustments;
        },

        saveAdjustments() {
            if (!this.currentAdjustments) return;

            // Create a new adjustments object with the updated values
            const newAdjustments = JSON.parse(
                JSON.stringify(this.fontAdjustments || {})
            );

            // Ensure browser and font size objects exist
            if (!newAdjustments[this.selectedBrowser]) {
                newAdjustments[this.selectedBrowser] = {};
            }

            if (!newAdjustments[this.selectedBrowser][this.selectedFontSize]) {
                newAdjustments[this.selectedBrowser][this.selectedFontSize] =
                    {};
            }

            // Update with current adjustments
            newAdjustments[this.selectedBrowser][this.selectedFontSize] = {
                ...this.currentAdjustments
            };

            // Save to store and API
            this.updateFontAdjustments(newAdjustments).then(() => {
                this.$message({
                    message: "Font adjustments saved successfully",
                    type: "success",
                    duration: 3000
                });

                // Force refresh line wrapping
                CalendarUtils.clearDescriptionLinesCache();
            });
        },

        resetAdjustments() {
            // Reset to stored values
            this.updateCurrentAdjustments();

            this.$message({
                message: "Adjustments reset to saved values",
                type: "info",
                duration: 2000
            });
        },

        applyForTesting() {
            if (!this.currentAdjustments) return;

            // Store the original method if not already in testing mode
            if (!this.isTestingMode) {
                this.originalFontMethod = CalendarUtils.getFontAdjustments;
            }

            // Override the getFontAdjustments method temporarily
            CalendarUtils.getFontAdjustments = () => {
                return this.currentAdjustments;
            };

            // Clear the cache
            CalendarUtils.clearDescriptionLinesCache();

            // Set testing mode flag
            this.isTestingMode = true;

            this.$message({
                message: "Adjustments applied temporarily for testing",
                type: "success",
                duration: 3000
            });

            this.$emit("adjustments-applied", this.currentAdjustments);
        },

        restoreOriginalSettings() {
            if (!this.isTestingMode || !this.originalFontMethod) return;

            // Restore original method
            CalendarUtils.getFontAdjustments = this.originalFontMethod;
            CalendarUtils.clearDescriptionLinesCache();

            // Reset testing mode flag
            this.isTestingMode = false;
            this.originalFontMethod = null;

            this.$message({
                message: "Original font settings restored",
                type: "info",
                duration: 2000
            });

            this.$emit("adjustments-restored");
        },

        async runFullCalibration() {
            this.calibrationRunning = true;
            this.formattedResults = "Calibrating fonts, please wait...";

            try {
                // Get the current browser type
                const browserType =
                    this.selectedBrowser ||
                    (this.detectedBrowser === "Firefox"
                        ? "firefox"
                        : this.detectedBrowser === "Edge"
                        ? "edge"
                        : this.detectedBrowser === "Safari"
                        ? "safari"
                        : "chrome");

                // Sample texts that represent common scenarios
                const sampleTexts = [
                    "Mexican Train Dominoes-SH",
                    "Bible Study W/ Scott-Library",
                    "Thanksgiving Dinner-Clubhouse",
                    "November Birthday Dessert Bar (DR)",
                    "Meeting with John Smith at Office"
                ];

                // Run the calibration on all font sizes
                const results = {};
                for (const fontSize of this.fontSizes) {
                    const fontResults = await FontCalibration.runCalibration(
                        sampleTexts,
                        fontSize
                    );
                    results[fontSize] = fontResults;
                }

                // Create browser-specific adjustments
                const adjustments = {};
                adjustments[browserType] = {};

                for (const [fontSize, fontResults] of Object.entries(results)) {
                    adjustments[browserType][fontSize] = {};

                    // For each font in the results
                    Object.entries(fontResults).forEach(
                        ([fontName, fontAdjustment]) => {
                            adjustments[browserType][fontSize][fontName] =
                                Math.round(fontAdjustment.adjustment || 2);
                        }
                    );
                }

                // Generate formatted output
                this.generateFormattedOutput(adjustments);

                this.$message({
                    message: "Calibration completed successfully",
                    type: "success",
                    duration: 3000
                });
            } catch (error) {
                console.error("Calibration failed:", error);
                this.formattedResults = "Calibration failed: " + error.message;

                this.$message({
                    message: "Calibration failed: " + error.message,
                    type: "error",
                    duration: 5000
                });
            } finally {
                this.calibrationRunning = false;
            }
        },

        generateFormattedOutput(adjustments) {
            // Create the output format that uses constants instead of hardcoded strings
            let output = `const fontAdjustments = {\n`;

            // Get all font constant names for reverse lookup
            const fontConstantNames = {};
            Object.entries(Fonts).forEach(([key, value]) => {
                fontConstantNames[value] = key;
            });

            // Add each browser section
            Object.entries(adjustments).forEach(
                ([browser, browserAdjustments]) => {
                    output += `  "${browser}": {\n`;

                    // Add each font size section
                    Object.entries(browserAdjustments).forEach(
                        ([fontSize, fontSizeAdjustments]) => {
                            output += `    "${fontSize}": {\n`;

                            // Add each font adjustment using constant names where possible
                            Object.entries(fontSizeAdjustments).forEach(
                                ([fontName, adjustment]) => {
                                    const constantName = fontConstantNames[
                                        fontName
                                    ]
                                        ? `Fonts.${fontConstantNames[fontName]}`
                                        : `"${fontName}"`;

                                    output += `      ${constantName}: ${adjustment},\n`;
                                }
                            );

                            output += `    },\n`;
                        }
                    );

                    output += `  },\n`;
                }
            );

            output += `};`;

            this.formattedResults = output;
        },

        copyToClipboard() {
            if (!this.formattedResults) return;

            const el = document.createElement("textarea");
            el.value = this.formattedResults;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);

            this.$message({
                message: "Results copied to clipboard",
                type: "success",
                duration: 2000
            });
        },

        saveCalibrationResults() {
            if (!this.formattedResults) return;

            try {
                // Extract the browser type from the formatted results
                const match = this.formattedResults.match(/"([^"]+)":\s*{/);
                const browserType = match ? match[1] : this.selectedBrowser;

                // Parse the current adjustments
                const startJson = this.formattedResults.indexOf("{");
                const endJson = this.formattedResults.lastIndexOf("}") + 1;

                // Create a properly formatted JSON string
                let jsonStr = this.formattedResults
                    .substring(startJson, endJson)
                    // First, handle the Fonts.CONSTANT syntax
                    .replace(
                        /(\s*)(Fonts\.[A-Z_]+)(\s*:)/g,
                        (match, before, fontRef, after) => {
                            const constName = fontRef.replace("Fonts.", "");
                            const fontValue = Fonts[constName] || constName;
                            return `${before}"${fontValue}"${after}`;
                        }
                    )
                    // Fix any remaining unquoted property names (safety check)
                    .replace(/([{,]\s*)([a-zA-Z0-9_\.]+)(\s*:)/g, '$1"$2"$3');

                console.log("Processed JSON string:", jsonStr);

                // Parse the adjustments
                const parsedAdjustments = JSON.parse(jsonStr);

                // Rest of the method remains the same
                const newAdjustments = JSON.parse(
                    JSON.stringify(this.fontAdjustments || {})
                );

                // Merge new calibration into existing adjustments
                Object.entries(parsedAdjustments).forEach(
                    ([browser, browserAdj]) => {
                        if (!newAdjustments[browser]) {
                            newAdjustments[browser] = {};
                        }

                        Object.entries(browserAdj).forEach(
                            ([fontSize, fontSizeAdj]) => {
                                newAdjustments[browser][fontSize] = fontSizeAdj;
                            }
                        );
                    }
                );

                // Save to store and API
                this.updateFontAdjustments(newAdjustments).then(() => {
                    this.$message({
                        message: "Calibration results saved successfully",
                        type: "success",
                        duration: 3000
                    });

                    // Update current adjustments view
                    this.updateCurrentAdjustments();

                    // Force refresh line wrapping
                    CalendarUtils.clearDescriptionLinesCache();
                });
            } catch (error) {
                console.error("Failed to save calibration results:", error);
                console.error("JSON string that failed to parse:", jsonStr);

                this.$message({
                    message:
                        "Failed to save calibration results: " + error.message,
                    type: "error",
                    duration: 5000
                });
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.font-calibration-tool {
  padding: 20px 0;
  
  .calibration-section {
    margin-bottom: 40px;
    padding: 20px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: #f9f9f9;
    
    h2 {
      margin-top: 0;
      margin-bottom: 20px;
      border-bottom: 1px solid #eee;
      padding-bottom: 10px;
      font-size: 18px;
    }
  }
  
  .control-panel {
    margin-bottom: 20px;
    
    .form-row {
      display: flex;
      flex-wrap: wrap;
      gap: 15px;
      margin-bottom: 15px;
      align-items: flex-end;
    }
    
    .form-group {
      label {
        display: block;
        margin-bottom: 5px;
        font-weight: bold;
      }
      
      &.checkbox-group {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      
      &.sample-text-group {
        flex: 1;
        min-width: 300px;
      }
      
      .hint {
        display: block;
        font-size: 12px;
        color: #666;
        margin-top: 5px;
      }
    }
    
    .example-texts {
      margin-top: 10px;
      
      .el-button {
        margin-right: 5px;
        margin-bottom: 5px;
      }
    }
  }
  
  .preview-area {
    .actions {
      margin-bottom: 15px;
    }
    
    .preview-container {
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 20px;
      background-color: white;
      
      h3 {
        margin-top: 0;
        margin-bottom: 15px;
        font-size: 16px;
      }
    }
    
    .calendar-cell {
      position: relative;
      width: 219px;
      height: 154px;
      border: 1px solid #333;
      margin-bottom: 20px;
      overflow: hidden;
      
      .date-number {
        position: absolute;
        top: 5px;
        right: 10px;
        font-weight: bold;
        font-size: 18px;
      }
      
      .cell-content {
        padding: 5px;
        padding-top: 25px;
        position: relative; 

        .time {
          width: 42px;
          max-width: 42px;
        }
        
        .description-lines {
          width: 167px;
        }
        
        .description {
          margin-bottom: 4px;
        }
        
        .clearfix {
          clear: both;
        }
        
        .float-left {
          float: left;
        }
        
        .float-right {
          float: right;
        }
        
        .text-right {
          text-align: right;
        }

        .width-boundary {
          &::after {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            height: 100%;
            border-right: 2px dotted rgba(245, 108, 108, 0.5);
          }
        }
      }
      
      .text-line {
        white-space: nowrap;
        overflow: hidden;
      }
      
      &.font-size-7, &.font-size-8 {
        line-height: 10pt;
      }
      
      &.font-size-9 {
        line-height: 9pt;
      }
      
      &.font-size-10 {
        line-height: 10pt;
      }
      
      &.font-size-11, &.font-size-12 {
        line-height: 11pt;
      }
    }
    
    .width-indicators {
      margin-top: 15px;
      
      .max-width-line {
        height: 2px;
        background-color: #1890ff;
        margin-bottom: 5px;
      }
      
      .max-width-label {
        font-size: 12px;
        color: #666;
        margin-bottom: 10px;
      }
      
      .width-info {
        font-size: 13px;
        color: #333;
        
        div {
          margin-bottom: 5px;
        }
      }
    }
  }
  
  .browser-selector {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    
    label {
      margin-right: 10px;
      font-weight: bold;
    }
    
    .ml-10 {
      margin-left: 10px;
    }
    
    .detected-browser {
      color: #666;
      font-style: italic;
    }
  }
  
  .font-adjustments-editor {
    .font-adjustments-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      gap: 10px;
      margin-bottom: 20px;
    }
    
    .font-adjustment-row {
      display: flex;
      align-items: center;
      
      .font-name {
        flex: 1;
        padding-right: 10px;
      }
    }
    
    .adjustment-actions {
      margin-top: 20px;
      display: flex;
      gap: 10px;
      justify-content: flex-end;
    }
  }
  
  .calibration-controls {
    margin-bottom: 20px;
    
    .calibration-info {
      margin-top: 10px;
      color: #666;
      font-style: italic;
    }
  }
  
  .calibration-results {
    margin-top: 20px;
    
    h3 {
      margin-bottom: 10px;
    }
    
    .results-code {
      max-height: 400px;
      overflow: auto;
      padding: 15px;
      background-color: #f0f0f0;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-family: monospace;
      margin-bottom: 15px;
    }
    
    .result-actions {
      display: flex;
      gap: 10px;
    }
  }

.adjustment-note {
  font-size: 11px;
  color: #888;
  margin-left: 5px;
}
}
</style>
